
import { supabase } from '@/integrations/supabase/client';

// Simple in-memory cache for redirects
const redirectCache: Record<string, string | null> = {};

/**
 * Get the destination path for a redirect if one exists
 * @param path The path to check for redirects
 * @returns The destination path or null if no redirect exists
 */
export const getRedirectDestination = async (path: string): Promise<string | null> => {
  // Use cache if available
  if (redirectCache[path] !== undefined) {
    return redirectCache[path];
  }
  
  try {
    // Get active redirect from database
    const { data, error } = await supabase
      .from('redirect_rules')
      .select('to_path')
      .eq('from_path', path)
      .eq('active', true)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching redirect:', error);
      return null;
    }
    
    // Cache the result (even if null)
    const destination = data?.to_path || null;
    redirectCache[path] = destination;
    
    return destination;
  } catch (error) {
    console.error('Error checking redirect for path:', path, error);
    return null;
  }
};

/**
 * Log redirect usage
 * @param fromPath The original path
 * @param toPath The destination path
 * @param referrer The referrer URL
 * @param userAgent The user agent string
 */
export const logRedirectUsage = async (
  fromPath: string,
  toPath: string,
  referrer?: string,
  userAgent?: string
): Promise<void> => {
  try {
    await supabase.from('redirect_usage_logs').insert({
      from_path: fromPath,
      to_path: toPath,
      referer: referrer,
      user_agent: userAgent
    });
  } catch (error) {
    console.warn('Error logging redirect usage:', error);
    // Non-critical operation, so we just log the error and continue
  }
};

/**
 * Handle redirect checking and logging
 * @param path The current path
 * @returns The destination path or null if no redirect exists
 */
export const handleRedirect = async (path: string): Promise<string | null> => {
  const destination = await getRedirectDestination(path);
  
  if (destination) {
    try {
      // Log the redirect usage
      await logRedirectUsage(
        path,
        destination,
        document.referrer,
        navigator.userAgent
      );
    } catch (error) {
      // Non-critical, just log the error
      console.warn('Error logging redirect:', error);
    }
  }
  
  return destination;
};

/**
 * Normalize a path for redirect lookups
 * @param path The path to normalize
 * @returns The normalized path
 */
export const normalizePath = (path: string): string => {
  // Remove trailing slashes except for root path
  let normalized = path === '/' ? path : path.replace(/\/+$/, '');
  
  // Convert to lowercase for case-insensitive matching
  normalized = normalized.toLowerCase();
  
  return normalized;
};

/**
 * Update the redirect rules in the database
 * @param rules The redirect rules to save
 * @returns A boolean indicating success or failure
 */
export const updateRedirectRules = async (rules: any[]): Promise<boolean> => {
  try {
    // First, get all existing rules
    const { data: existingRules, error: fetchError } = await supabase
      .from('redirect_rules')
      .select('id, from_path');
      
    if (fetchError) {
      console.error('Error fetching existing redirect rules:', fetchError);
      return false;
    }
    
    // Create a map of existing rules by from_path for quick lookups
    const existingRulesMap = new Map();
    existingRules?.forEach(rule => {
      existingRulesMap.set(rule.from_path, rule.id);
    });
    
    // Prepare arrays for upsert and delete operations
    const rulesToUpsert = [];
    const ruleIdsToDelete = [];
    
    // Process each rule from the input
    for (const rule of rules) {
      const ruleData = {
        from_path: rule.from,
        to_path: rule.to,
        notes: rule.notes || '',
        active: rule.active !== false
      };
      
      // If this rule exists, add its ID for update
      if (existingRulesMap.has(rule.from)) {
        ruleData['id'] = existingRulesMap.get(rule.from);
        existingRulesMap.delete(rule.from); // Remove from map to track deletions
      }
      
      rulesToUpsert.push(ruleData);
    }
    
    // Any remaining rules in the map should be deleted
    existingRulesMap.forEach((id) => {
      ruleIdsToDelete.push(id);
    });
    
    // Perform upsert operation for new and updated rules
    if (rulesToUpsert.length > 0) {
      const { error: upsertError } = await supabase
        .from('redirect_rules')
        .upsert(rulesToUpsert);
        
      if (upsertError) {
        console.error('Error upserting redirect rules:', upsertError);
        return false;
      }
    }
    
    // Perform delete operation for removed rules
    if (ruleIdsToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('redirect_rules')
        .delete()
        .in('id', ruleIdsToDelete);
        
      if (deleteError) {
        console.error('Error deleting redirect rules:', deleteError);
        return false;
      }
    }
    
    // Clear the cache after successful update
    for (const key in redirectCache) {
      delete redirectCache[key];
    }
    
    return true;
  } catch (error) {
    console.error('Error updating redirect rules:', error);
    return false;
  }
};

/**
 * Reload redirects from the database (clears cache)
 */
export const reloadRedirects = async (): Promise<void> => {
  // Clear the cache
  for (const key in redirectCache) {
    delete redirectCache[key];
  }
  
  console.log('Redirect cache cleared, rules will be reloaded on next use');
};
